import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import { Pencil } from "@dreamhub/designsystem/icons";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useTermsVersions } from "@/pages/contracts/components/termsHistory/hooks";
import { findPendingVersion } from "@/pages/contracts/components/termsHistory/pendingVersion";
import { formatDateShort } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import UpdateTermsModal from "./UpdateTermsModal";
import { useTermsFieldSettings } from "./hooks";
import { TermsUpdateKind } from "./types";
import { UPDATE_KIND_SEARCH_PARAM } from "./UpdateTermsPage";

interface Props {
  contractId: string;
}

// Gates on `loading` too: `useFeatureFlag` starts false, so `enabled` alone flashes.
const UpdateTermsAction: React.FC<Props> = ({ contractId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enabled, loading } = useFeatureFlag(
    FeatureFlags.CRM_9208_CONTRACT_AMENDMENTS
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: fieldSettings } = useTermsFieldSettings(contractId, {
    enabled: !loading && enabled
  });
  // Same query key as the Terms & Services tab this button sits on, so the
  // picker's note costs no extra request.
  const {
    data: versionsData,
    isLoading: isLoadingVersions,
    isError: isVersionsError
  } = useTermsVersions(contractId, {
    enabled: !loading && enabled
  });

  if (loading || !enabled) return null;

  const canUpdateTerms = fieldSettings?.canUpdateTerms ?? false;
  const pendingAmendment = findPendingVersion(versionsData?.versions);
  // Informational only: the picker disables the Amendment card, and a
  // correction's save confirms cancelling the scheduled amendment.
  const hasPendingAmendment = !!pendingAmendment?.effectiveDate;
  // Until the list resolves, "none scheduled" and "not known yet" look the same,
  // and the amendment route past this button has no second guard.
  const isPendingStateUnknown = isLoadingVersions || isVersionsError;

  const tooltipText = () => {
    // The assignment reason leads: the financials permission does not open a
    // contract the role may not edit, and a standalone contract always reads
    // as locked.
    if (!canUpdateTerms) {
      if (fieldSettings?.isAssignmentRestricted)
        return t("common.editRestrictions.notAssigned");
      return t(
        fieldSettings?.isFinancialsLocked
          ? "contracts.updateTerms.lockedTooltip"
          : "common.noEditPermission"
      );
    }
    if (hasPendingAmendment)
      return t("contracts.updateTerms.pendingAmendmentTooltip", {
        date: formatDateShort(pendingAmendment!.effectiveDate!)
      });
    if (isPendingStateUnknown)
      return t(
        isVersionsError
          ? "contracts.updateTerms.editor.scheduledCheckFailed"
          : "contracts.updateTerms.editor.checkingScheduled"
      );
    return undefined;
  };

  return (
    <>
      <Button
        variant="secondary"
        disabled={!canUpdateTerms || isPendingStateUnknown}
        tooltipText={tooltipText()}
        onClick={() => setIsModalOpen(true)}
        data-test="contract-update-terms-button"
      >
        <IconContainer
          as="span"
          size="sm"
          color="secondary"
          className="shrink-0"
        >
          <Pencil />
        </IconContainer>
        {t("contracts.updateTerms.action")}
      </Button>
      <UpdateTermsModal
        isOpen={isModalOpen}
        pendingAmendment={pendingAmendment}
        onClose={() => setIsModalOpen(false)}
        onContinue={(kind: TermsUpdateKind) => {
          setIsModalOpen(false);
          navigate(
            `/contracts/${contractId}/terms/update?${UPDATE_KIND_SEARCH_PARAM}=${kind}`
          );
        }}
      />
    </>
  );
};

export default UpdateTermsAction;
